import { useCallback, useEffect, useMemo, useState } from "react";
import { InstructorLink } from "@models/ISchema";
import { IGroupValue, ILeague } from "@modules/vacancies/contextVacancies/ContextVacancies";
import { useTranslation } from "react-i18next";
import {
  setAssignationHaveChangesForLink,
  setAssignationTypesToSave,
  setInstructorLinkTagsToSave,
  useVacanciesStore,
} from "@modules/vacancies/contextVacancies/useVacanciesStore";
import { useSettingsStore } from "@context/useSettingsStore";
import { isUnassignment } from "@modules/vacancies/utils";
import { getAssignationLimitRule, getUdcLimitThreshold } from "@utils/assignationLimits";
import {
  InstructorLinkTags,
  InstructorLinkTagValue,
} from "@modules/vacancies/Instructor/instructorLinkTags/types";
import {
  INSTRUCTOR_LINK_TAG_KEYS,
  parseInstructorLinkTags,
} from "@modules/vacancies/Instructor/instructorLinkTags/utils";
import {
  TagDateError,
  validateTagDates,
} from "@modules/vacancies/Instructor/instructorLinkTags/validation";

interface useInstructorTableInputsStateProps {
  instructors: InstructorLink[];
  linkTotalBlocks: number;
  linkId: string;
  link: ILeague;
  isEditing: boolean;
  isAssignationTypeEnabled?: boolean;
  onChange?: (values: IGroupValue[]) => void;
}

/**
 * Custom hook to manage the state of instructor table inputs.
 *
 * @param props - Optional properties to initialize the state.
 * @param props.instructors - Array of instructors to initialize the state.
 * @param props.linkId - Unique identifier for the link.
 * @param props.linkTotalBlocks - Total number of blocks for the link.
 *
 * @returns An object containing:
 * - `countResponsibility`: Array of instructor group values.
 * - `updateResponsibilityValues`: Function to update responsibility values for an instructor.
 * - `getInstructorValues`: Function to get the values of a specific instructor.
 *
 * @example
 * ```typescript
 * const { countResponsibility, updateResponsibilityValues, getInstructorValues } = useInstructorTableInputsState({
 *   instructors: [...],
 *   linkId: 'link123',
 *   linkTotalBlocks: 100,
 * });
 *
 * // Update responsibility values
 * updateResponsibilityValues(50, 'link123', 'instructor456', 'responsibility');
 *
 * // Get instructor values
 * const instructorValues = getInstructorValues('instructor456');
 * ```
 */
const useInstructorTableInputsState = (props?: useInstructorTableInputsStateProps) => {
  const {
    instructors = [],
    linkId,
    linkTotalBlocks,
    link,
    isEditing,
    isAssignationTypeEnabled = false,
    onChange,
  } = props || {};
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [countResponsibility, setCountResponsibility] = useState<Array<IGroupValue>>(null);
  const [assignationType, setAssignationType] = useState<{ [key: string]: any } | null>(null);
  const [ogAssignationType, setOgAssignationType] = useState<{ [key: string]: any } | null>(null);
  const assignationTypesToSave = useVacanciesStore(state => state.assignationTypesToSave);

  const instructorLinkTagsSetting = useSettingsStore(state => state.instructorLinkTags);
  const adaptedAssignmentsByCourseType = useSettingsStore(
    state => state.adaptedAssignmentsByCourseType,
  );
  const isInstructorLinkTagsEnabled = !!instructorLinkTagsSetting?.enabled;
  const term = link?.bundle?.term;
  const courseType = link?.course?.courseType;
  const [tagsByInstructorLink, setTagsByInstructorLink] = useState<
    Record<string, InstructorLinkTags>
  >({});

  const updateAssignationType = (
    instructorId: string,
    instructorLinkId: string,
    assignationTypeValue: any,
  ) => {
    const newValue = {
      ...assignationType,
      [instructorId]: assignationTypeValue || null,
    };

    if (!assignationTypeValue && !ogAssignationType?.[instructorId]) {
      delete newValue[instructorId];
    }

    setAssignationType(newValue);

    const newAssignationTypes = assignationTypesToSave.filter(
      item => !(item.instructorId === instructorId && item.linkId === linkId),
    );

    if (assignationTypeValue || ogAssignationType?.[instructorId]) {
      newAssignationTypes.push({
        data: assignationTypeValue?.data || null,
        instructorId,
        instructorLinkId,
        linkId,
      });
    }

    setAssignationTypesToSave([...newAssignationTypes]);
  };

  const setInitialState = () => {
    setCountResponsibility(
      instructors.map((instructor: InstructorLink) => {
        const blockPercent = (instructor.totalBlocks * 100) / linkTotalBlocks;
        const groupValue: IGroupValue = {
          linkId,
          instructor,
          blockPercent: blockPercent ? Math.round(blockPercent) : 0,
          teachingHoursPercentage: instructor.teachingHoursPercentage || 0,
          paymentPercentage: instructor.paymentPercentage || 0,
          isPrimary: instructor.isPrimary || false,
        };

        return groupValue;
      }),
    );

    const settingTags = instructorLinkTagsSetting?.tags ?? [];

    const initialTags = instructors.reduce(
      (acc, instructor: InstructorLink) => {
        if (isUnassignment(instructor?.instructor?.id)) return acc;

        const backendTags = parseInstructorLinkTags(instructor.tags);

        // Seed every tag defined by the setting (current value or null) so that
        // editing one tag still sends the whole set in the changeset.
        acc[instructor.id] = settingTags.reduce((tags, def) => {
          tags[def.key] = backendTags[def.key] ?? null;
          return tags;
        }, {} as InstructorLinkTags);

        return acc;
      },
      {} as Record<string, InstructorLinkTags>,
    );

    setTagsByInstructorLink(initialTags);
  };

  /**
   * Applies (or reverts) the group-drop cascade for a teacher: a registered
   * Baja de grupo date forces responsibility/load to 0 and clears titularidad;
   * clearing the date restores the teacher's original values.
   */
  const applyDropCascade = (instructorId: string, dropped: boolean) => {
    const updated = [...(countResponsibility ?? [])].map(data => {
      if (data?.instructor?.instructor?.id !== instructorId) return data;

      if (dropped) {
        return { ...data, teachingHoursPercentage: 0, paymentPercentage: 0, isPrimary: false };
      }

      const origin = instructors.find(item => item?.instructor?.id === instructorId);

      return {
        ...data,
        teachingHoursPercentage: origin?.teachingHoursPercentage || 0,
        paymentPercentage: origin?.paymentPercentage || 0,
        isPrimary: origin?.isPrimary || false,
      };
    });

    setCountResponsibility(updated);
    onChange?.(updated);
  };

  /** Updates a single tag value for an instructor link and syncs the save list. */
  const updateTagValue = (
    instructorLink: InstructorLink,
    key: string,
    value: InstructorLinkTagValue,
  ) => {
    const instructorLinkId = instructorLink.id;
    const newTags = { ...(tagsByInstructorLink[instructorLinkId] ?? {}), [key]: value };

    setTagsByInstructorLink(prev => ({ ...prev, [instructorLinkId]: newTags }));

    const others = useVacanciesStore
      .getState()
      .instructorLinkTagsToSave.filter(item => item.instructorLinkId !== instructorLinkId);

    setInstructorLinkTagsToSave([...others, { instructorLinkId, linkId, tags: newTags }]);

    if (key === INSTRUCTOR_LINK_TAG_KEYS.dropDate) {
      applyDropCascade(instructorLink?.instructor?.id, Boolean(value));
    }
  };

  /** Date-validation errors per instructor link (empty when the feature is off). */
  const tagDateErrors = useMemo(() => {
    if (!isInstructorLinkTagsEnabled) return {} as Record<string, TagDateError[]>;

    const period = { start: term?.start_date, end: term?.end_date };

    return Object.entries(tagsByInstructorLink).reduce(
      (acc, [instructorLinkId, tags]) => {
        const errors = validateTagDates(tags, period);
        if (errors.length) acc[instructorLinkId] = errors;
        return acc;
      },
      {} as Record<string, TagDateError[]>,
    );
  }, [isInstructorLinkTagsEnabled, tagsByInstructorLink, term?.start_date, term?.end_date]);

  const hasDropDate = (instructorLinkId: string) =>
    Boolean(tagsByInstructorLink[instructorLinkId]?.[INSTRUCTOR_LINK_TAG_KEYS.dropDate]);

  const instructorsById = useMemo(() => {
    const map = new Map<string, InstructorLink>();

    instructors.forEach(instructor => {
      if (!isUnassignment(instructor?.instructor?.id)) {
        map.set(instructor.instructor.id, instructor);
      }
    });

    return map;
  }, [instructors]);

  const setInitialAssignationType = useCallback(() => {
    const initialAssignationTypes: { [key: string]: any } = {};

    Array.from(instructorsById.values()).forEach((instructor: InstructorLink) => {
      if (instructor.assignationType) {
        initialAssignationTypes[instructor?.instructor?.id] = instructor.assignationType
          ? {
              label: instructor.assignationType.name,
              value: instructor.assignationType.id,
              data: instructor.assignationType,
            }
          : null;
      }
    });

    setAssignationType(initialAssignationTypes);
    setOgAssignationType(initialAssignationTypes);
  }, [instructorsById]);

  const assignationTypeHasChanges = useMemo(() => {
    if (!isAssignationTypeEnabled || !assignationType) return false;

    const assignationTypeEntries = Object.entries(assignationType);
    const originalAssignationsCount = Object.entries(ogAssignationType ?? {}).length;

    if (assignationTypeEntries.length !== originalAssignationsCount) {
      return true;
    }

    return Object.entries(assignationType).some(([instructorId, assignation]) => {
      const originalAssignation = instructorsById.get(instructorId)?.assignationType;
      const originalAssignationId = originalAssignation ? originalAssignation.id : null;
      const currentAssignationId = assignation ? assignation.data.id : null;

      return originalAssignationId !== currentAssignationId;
    });
  }, [isAssignationTypeEnabled, assignationType, instructorsById]);

  const someAssignationTypeInvalid = useMemo(() => {
    // Limit errors only apply while editing; pre-existing over-limit data is
    // reported as soon as edit mode starts, from the initial assignations.
    if (!isAssignationTypeEnabled || !isEditing) return [];

    const countByCode = new Map<
      string,
      { count: number; max: number; name?: string; code?: string }
    >();

    Object.values(assignationType ?? {}).forEach(assignation => {
      const data = assignation?.data ?? {};
      const code = data?.code;
      const max = data?.maxAssignmentsByLink;

      if (!code) return;

      const value = countByCode.get(code) ?? { count: 0, max, name: data?.name, code };
      value.count += 1;
      value.max = max;

      countByCode.set(code, value);
    });

    return Array.from(countByCode.values())
      .filter(value => value.max && value.count > value.max)
      ?.map(value => {
        const udcThreshold = getUdcLimitThreshold(
          getAssignationLimitRule(adaptedAssignmentsByCourseType, courseType, value.code),
        );

        // The UdC limit mode has its own message, distinct from the fixed-limit one.
        const error =
          udcThreshold !== null
            ? {
                type: "ERROR" as const,
                message: t("bundle.link-card.errors.assignation-udc-limit", { udc: udcThreshold }),
              }
            : {
                type: "ERROR" as const,
                messageAccent: `${t("bundle.link-card.errors.assignation-capacity-exceeded.accent")}:`,
                message: `${t("bundle.link-card.errors.assignation-capacity-exceeded.message", {
                  role: value.name,
                  max: value.max,
                })}`,
              };

        return {
          code: value.code,
          link,
          maxAllowed: value.max,
          error,
        };
      });
  }, [
    isAssignationTypeEnabled,
    isEditing,
    assignationType,
    adaptedAssignmentsByCourseType,
    courseType,
    language,
  ]);

  /**
   * Updates the responsibility values for an instructor based on the provided parameters.
   *
   * @param newValue - The new value to be set. It can be a number or a boolean.
   * @param linkId - The unique identifier for the link.
   * @param instructorId - The unique identifier for the instructor.
   * @param typeParam - The type of parameter to update. It can be "responsibility", "payment", or "isPrimary".
   *
   * @remarks
   * - If `typeParam` is "responsibility", both `teachingHoursPercentage` and `paymentPercentage` will be updated with `newValue`.
   * - If `typeParam` is "payment", only `paymentPercentage` will be updated with `newValue`.
   * - If `typeParam` is "isPrimary", `isPrimary` will be updated with `newValue`. Additionally, if `newValue` is `true`, all other instructors with the same `linkId` will have their `isPrimary` set to `false`.
   *
   * @example
   * ```typescript
   * updateResponsibilityValues(50, 'link123', 'instructor456', 'responsibility');
   * updateResponsibilityValues(30, 'link123', 'instructor456', 'payment');
   * updateResponsibilityValues(true, 'link123', 'instructor456', 'isPrimary');
   * ```
   */
  const updateResponsibilityValues = (
    newValue: number | boolean,
    linkId: string,
    instructorId: string,
    typeParam: "responsibility" | "payment" | "isPrimary",
  ) => {
    const updatedResponsibility = [...(countResponsibility ?? [])].map(data => {
      if (linkId === data.linkId && instructorId === data?.instructor?.instructor.id) {
        const value = newValue ? Number(String(newValue).replace(/\..*$/, "")) : newValue;

        if (typeParam === "responsibility") {
          return {
            ...data,
            teachingHoursPercentage: value as number,
            paymentPercentage: value as number,
          };
        } else if (typeParam === "payment") {
          return {
            ...data,
            paymentPercentage: value as number,
          };
        } else if (typeParam === "isPrimary") {
          return { ...data, isPrimary: newValue as boolean };
        }
      }

      if (
        typeParam === "isPrimary" &&
        newValue === true &&
        data?.instructor.instructor.id !== instructorId &&
        linkId === data.linkId
      ) {
        return { ...data, isPrimary: false };
      }

      return data;
    });

    setCountResponsibility(updatedResponsibility);
    onChange?.(updatedResponsibility);
  };

  useEffect(() => {
    if (!countResponsibility || !isEditing) setInitialState();

    if (!isEditing && isAssignationTypeEnabled) {
      setInitialAssignationType();
      setAssignationTypesToSave([]);
    }

    if (!isEditing && isInstructorLinkTagsEnabled) {
      setInstructorLinkTagsToSave([]);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isAssignationTypeEnabled) return;
    setAssignationHaveChangesForLink(linkId, assignationTypeHasChanges);

    return () => setAssignationHaveChangesForLink(linkId, null);
  }, [isAssignationTypeEnabled, assignationTypeHasChanges, linkId]);

  return {
    countResponsibility,
    updateResponsibilityValues,
    assignationType,
    updateAssignationType,
    someAssignationTypeInvalid,
    assignationTypeHasChanges,
    tagsByInstructorLink,
    updateTagValue,
    tagDateErrors,
    hasDropDate,
    isInstructorLinkTagsEnabled,
  };
};

export default useInstructorTableInputsState;
